/**
 * Pure helpers for the product-form barcode scanning.
 *
 * Kept separate from the components so the decode / capability rules are
 * unit-testable without a DOM or a camera.
 */

/**
 * Scanners (USB keyboard-wedge and camera alike) can emit stray whitespace or a
 * trailing CR/LF from their suffix configuration. Strip it before it reaches the
 * form, or the value fails the API's barcode validation.
 */
export function normalizeScannedBarcode(raw: string): string {
  return raw.replace(/[\r\n\t]/g, '').trim();
}

/**
 * A scan is only accepted when it leaves a non-empty code. Guards against a
 * decoder emitting an empty frame result, which would silently blank a barcode
 * the user had already typed.
 */
export function isAcceptableScan(raw: string): boolean {
  return normalizeScannedBarcode(raw).length > 0;
}

/**
 * Camera scanning needs both a secure context (getUserMedia is gated on
 * HTTPS/localhost) and the mediaDevices API. The Admin Dashboard commonly runs
 * on a desktop with no camera at all, so the UI must be able to ask before
 * offering the option.
 */
export function isCameraScanSupported(nav?: {
  mediaDevices?: { getUserMedia?: unknown };
}, isSecureContext = true): boolean {
  if (!isSecureContext) return false;
  return typeof nav?.mediaDevices?.getUserMedia === 'function';
}

/**
 * Maps a getUserMedia failure to the message shown in the scanner overlay.
 * Every branch points the user at an alternative that still works, since the
 * barcode field itself is always typeable.
 */
export function cameraErrorMessage(err: unknown): string {
  const msg = err instanceof Error ? `${err.name}: ${err.message}` : String(err);
  if (msg.includes('NotAllowedError') || msg.includes('Permission')) {
    return 'Camera permission denied. Allow camera access in your browser settings, or use a USB scanner / type the barcode.';
  }
  if (msg.includes('NotFoundError') || msg.includes('DevicesNotFound') || msg.includes('no camera')) {
    return 'No camera found on this device. Use a USB barcode scanner or type the barcode.';
  }
  if (msg.includes('NotReadableError') || msg.includes('TrackStartError')) {
    return 'The camera is already in use by another application. Close it and try again.';
  }
  return `Camera error: ${msg}`;
}
